import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import "./Template_2.css";
import Template_2_Header from "./Template_2_Header";
import Template_2_Footer from "./Template_2_Footer";
import Template_2_EnquiryPopup from "./Template_2_EnquiryPopup";
import Template_2_TopHeader from "./Template_2_TopHeader";

const Template_2_PackageDetails = () => {
  const { packageId } = useParams();
  const navigate = useNavigate();

  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showEnquiryPopup, setShowEnquiryPopup] = useState(false);

  useEffect(() => {
    const getPackageDetails = async () => {
      if (!packageId) return;
      setLoading(true);
      try {
        const response = await apiService.get(
          `${config.GETPACKAGEDETAILS}/${packageId}`
        );
        setDetails(response);
      } catch (err) {
        console.error("Failed to fetch package details:", err);
        setError("Could not load package details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    getPackageDetails();
  }, [packageId]);

  if (loading)
    return <div className="Template_2 loading">Loading package details...</div>;
  if (error) return <div className="Template_2 error">{error}</div>;
  if (!details)
    return <div className="Template_2 error">Package not found</div>;

  // Extract data from the API response
  const pkg = details.package || {};
  const itineraries = details.itineraries || [];
  const includes = details.includes || [];
  const excludes = details.excludes || [];
  const flightBookings = details.flightBookings || [];
  const hotelBookings = details.hotelBookings || [];
  const termsandConditions = details.termsandConditions || [];
  const transfers = details.transfers || [];
  const sightSeeings = details.sightSeeings || [];

  return (
    <div className="Template_2 package-detail Template_2 active">
      <Template_2_TopHeader />
      <Template_2_Header />

      {/* Main Content */}
      <div className="Template_2 detail-content">
        <div className="Template_2 container">
          <div className="Template_2 detail-top-section">
            {/* Left Side - Image */}
            <div className="Template_2 detail-image-container">
              <img
                src={pkg.imageSrc}
                alt={pkg.packageName}
                className="Template_2 detail-image"
              />
            </div>

            {/* Right Side - Booking Box */}
            <div className="Template_2 detail-booking-box">
              <div className="Template_2 booking-card">
                <h3 className="Template_2 booking-title">Book This Package</h3>
                <div className="Template_2 booking-price">
                  INR {pkg.price?.toLocaleString("en-IN") || "350,000"}
                </div>
                <div className="Template_2 booking-info">
                  <div className="Template_2 booking-info-item">
                    <i className="fas fa-clock"></i>
                    <div>
                      <span>Duration</span>
                      <span>{pkg.duration}</span>
                    </div>
                  </div>
                  <div className="Template_2 booking-info-item">
                    <i className="fas fa-user-friends"></i>
                    <div>
                      <span>For</span>
                      <span>{pkg.noOfPersons} Persons</span>
                    </div>
                  </div>
                  <div className="Template_2 booking-info-item">
                    <i className="fas fa-user"></i>
                    <div>
                      <span>Contact</span>
                      <span>{pkg.contactName}</span>
                    </div>
                  </div>
                  <div className="Template_2 booking-info-item">
                    <i className="fas fa-phone"></i>
                    <div>
                      <span>Phone</span>
                      <span>{pkg.contactPhoneNumber}</span>
                    </div>
                  </div>
                </div>
                <button
                  className="Template_2 enquire-btn"
                  onClick={() => setShowEnquiryPopup(true)}
                >
                  Enquire Now
                </button>
              </div>
            </div>
          </div>

          {/* Package Details Below */}
          <div className="Template_2 detail-bottom-section">
            {/* Package Name and Info */}
            <div className="Template_2 package-header-info">
              <h1 className="Template_2 package-name">{pkg.packageName}</h1>
              <div className="Template_2 package-quick-info">
                <div className="Template_2 package-info-item">
                  <i className="fas fa-clock"></i>
                  <span>{pkg.duration}</span>
                </div>
                <div className="Template_2 package-info-item">
                  <i className="fas fa-user-friends"></i>
                  <span>{pkg.noOfPersons} Persons</span>
                </div>
              </div>
            </div>

            {/* Overview */}
            {pkg.description && (
              <div className="Template_2 detail-section">
                <h2 className="Template_2 section-title">Overview</h2>
                <div
                  className="Template_2 detail-description"
                  dangerouslySetInnerHTML={{
                    __html: pkg.description.replace(/\r\n/g, "<br/>"),
                  }}
                />
              </div>
            )}
            {/* Flight Details */}
            {flightBookings.length > 0 && (
              <div className="Template_2 detail-section">
                <h2 className="Template_2 section-title">Flight Details</h2>
                <div className="Template_2 flights-container">
                  {flightBookings.map((flight, i) => (
                    <div key={i} className="Template_2 flight-card">
                      <div className="Template_2 flight-header">
                        <h4>
                          {flight.airline} - {flight.flightNumber}
                        </h4>
                        <span className="Template_2 flight-route">
                          {flight.departureCity} → {flight.arrivalCity}
                        </span>
                      </div>
                      <div className="Template_2 flight-details">
                        <span>Class: {flight.cabinClass}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Hotel Accommodations */}
            {hotelBookings.length > 0 && (
              <div className="Template_2 detail-section">
                <h2 className="Template_2 section-title">
                  Hotel Accommodations
                </h2>
                <div className="Template_2 hotels-grid">
                  {hotelBookings.map((hotel, i) => (
                    <div key={i} className="Template_2 hotel-card">
                      <h4>{hotel.hotelName}</h4>
                      <div className="Template_2 hotel-location">
                        <i className="fas fa-map-marker-alt"></i>
                        {hotel.city}
                      </div>
                      <div className="Template_2 hotel-details">
                        <span>Room Type: {hotel.roomType}</span>
                        <span>Nights: {hotel.nights}</span>
                      </div>
                      <div className="Template_2 hotel-dates">
                        <span>
                          Check-in:{" "}
                          {new Date(hotel.checkInDate).toLocaleDateString()}
                        </span>
                        <span>
                          Check-out:{" "}
                          {new Date(hotel.checkOutDate).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Sightseeing */}
            {sightSeeings.length > 0 && (
              <div className="Template_2 detail-section">
                <h2 className="Template_2 section-title">Sightseeing</h2>
                <div className="Template_2 sightseeing-grid">
                  {sightSeeings.map((sight, i) => (
                    <div key={i} className="Template_2 sightseeing-card">
                      <h4>{sight.sightseeingName}</h4>
                      <div className="Template_2 sightseeing-location">
                        <i className="fas fa-map-marker-alt"></i>
                        {sight.destination}
                      </div>
                      <p>{sight.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Transfers */}
            {transfers.length > 0 && (
              <div className="Template_2 detail-section">
                <h2 className="Template_2 section-title">Transfers</h2>
                <div className="Template_2 transfers-container">
                  {transfers.map((transfer, i) => (
                    <div key={i} className="Template_2 transfer-card">
                      <div className="Template_2 transfer-icon">
                        <i className="fas fa-car"></i>
                      </div>
                      <div className="Template_2 transfer-details">
                        <h4>Transfer {i + 1}</h4>
                        <div className="Template_2 transfer-info">
                          <span>
                            <strong>From:</strong> {transfer.fromLocation}
                          </span>
                          <span>
                            <strong>To:</strong> {transfer.toLocation}
                          </span>
                          <span>
                            <strong>Vehicle:</strong> {transfer.vehicleDetails}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Itinerary */}
            {itineraries.length > 0 && (
              <div className="Template_2 detail-section">
                <h2 className="Template_2 section-title">Itinerary</h2>
                <div className="Template_2 itinerary-container">
                  {itineraries.map((day, i) => (
                    <div key={i} className="Template_2 itinerary-day">
                      <div className="Template_2 itinerary-header">
                        <div className="Template_2 day-number">Day {i + 1}</div>
                        <h3>{day.location}</h3>
                      </div>
                      <p className="Template_2 itinerary-description">
                        {day.description}
                      </p>
                      {day.meals && (
                        <div className="Template_2 itinerary-meals">
                          <i className="fas fa-utensils"></i>
                          <strong>Meals:</strong> {day.meals}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Included */}
            {includes.length > 0 && (
              <div className="Template_2 detail-section">
                <h2 className="Template_2 section-title">What's Included</h2>
                <ul className="Template_2 included-list">
                  {includes.map((item, idx) => (
                    <li key={idx}>
                      <i className="fas fa-check"></i>
                      {item.description}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Excluded */}
            {excludes.length > 0 && (
              <div className="Template_2 detail-section">
                <h2 className="Template_2 section-title">
                  What's Not Included
                </h2>
                <ul className="Template_2 excluded-list">
                  {excludes.map((item, idx) => (
                    <li key={idx}>
                      <i className="fas fa-times"></i>
                      {item.description}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Terms & Conditions */}
            {termsandConditions.length > 0 && (
              <div className="Template_2 detail-section">
                <h2 className="Template_2 section-title">Terms & Conditions</h2>
                <div className="Template_2 terms-list">
                  {termsandConditions.map((term, i) => (
                    <div key={i} className="Template_2 term-item">
                      <strong>{term.title}:</strong> {term.description}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Template_2_Footer />
      {showEnquiryPopup && (
        <Template_2_EnquiryPopup
          packageDetails={pkg}
          onClose={() => setShowEnquiryPopup(false)}
        />
      )}
    </div>
  );
};

export default Template_2_PackageDetails;
